/**
 * /game（匹克硬邦幫）的 SEO 內容：React 頁面與 scripts/generate-static-pages.cjs 的預渲染、
 * JSON-LD 共用同一份，確保爬蟲讀到的跟使用者看到的一致。
 * 內容只寫遊戲真的有的功能（沒有連線對戰，就不要寫）。
 */

export const GAME_SEO = {
  title: '匹克球遊戲｜匹克硬邦幫：免費線上 3D 匹克球對戰，手機免下載',
  description:
    '免費線上匹克球遊戲「匹克硬邦幫」：打開瀏覽器就能玩，手機觸控、電腦鍵盤都可以。3D 真實物理，雙彈跳、廚房規則照比賽判；六支球拍各有必殺技，還有殺球、飛身撲接、無雙覺醒與電視輔助判決。',
  /** 遊戲最近一次有感更新的日期（sitemap lastmod、JSON-LD dateModified 用），改版時手動更新 */
  updated: '2026-10-23',
  /** 第一版上線日 */
  published: '2025-11-16',
};

/** 遊戲畫面截圖（public/game/shots/），頁面圖庫、JSON-LD screenshot、sitemap image 共用 */
export const GAME_SHOTS = [
  { src: '/game/shots/special-inferno.webp', alt: '匹克球遊戲必殺技畫面：烈焰爆裂抽擊切入演出', caption: '必殺技：集滿能量，一拍放出' },
  { src: '/game/shots/special-phantom.webp', alt: '匹克球遊戲必殺技畫面：幻影分身，球分裂成三顆', caption: '幻影分身：三顆球只有一顆是真的' },
  { src: '/game/shots/smash.webp', alt: '匹克球遊戲殺球畫面：跳起來把機會球殺下去', caption: '機會球跳起來殺' },
  { src: '/game/shots/dive.webp', alt: '匹克球遊戲飛身撲接畫面：跑不到的球撲過去救', caption: '跑不到？飛身撲接' },
  { src: '/game/shots/awaken.webp', alt: '匹克球遊戲無雙覺醒畫面：落後時發動逆轉', caption: '無雙覺醒：落後也能逆轉' },
  { src: '/game/shots/review.webp', alt: '匹克球遊戲電視輔助判決畫面：鷹眼顯示球落在線內', caption: '壓線球？申請電視輔助判決' },
  { src: '/game/shots/select.webp', alt: '匹克球遊戲選角畫面：五位選手各有專屬絕招', caption: '五位選手，各有一招絕招' },
  { src: '/game/shots/celebrate.webp', alt: '匹克球遊戲得分慶祝畫面：贏球後的跳舞挑釁', caption: '得分了就跳舞挑釁' },
];

/** 常見問題：頁面上看得到，也輸出成 FAQPage */
export const GAME_FAQ = [
  {
    q: '有免費的匹克球遊戲可以玩嗎？',
    a: '有。「匹克硬邦幫」是完全免費的線上匹克球遊戲，不用註冊、不用下載，打開 picklemastertw.com/game 就能開打。',
  },
  {
    q: '手機可以玩嗎？要下載 App 嗎？',
    a: '不用下載 App，iPhone 和 Android 直接用瀏覽器開就能玩。開打後畫面會鋪滿全螢幕，左手用搖桿跑位、右手按「軟／抽／挑」擊球，橫拿手機畫面更大。',
  },
  {
    q: '電腦版怎麼操作？',
    a: 'WASD 或方向鍵移動，J 放小球（dink）、K 抽球或殺球、L 挑高球。能量滿了按空白鍵蓄勢放必殺，U 放角色絕招，Shift 飛身撲接，Q 發動無雙覺醒，壓線球判決不利時按 C 申請電視輔助判決，Esc 暫停。',
  },
  {
    q: '遊戲的規則跟真的匹克球一樣嗎？',
    a: '一樣照比賽規則判：雙彈跳、廚房（非截擊區）、只有發球方能得分的換發球制、觸身與出界都會判，犯規時會告訴你是哪一條規則。球的彈性、球拍反彈係數與空氣阻力也照真實數據模擬。想專心練規則可以切到寫實模式，關掉必殺技。',
  },
  {
    q: '有哪些必殺技和絕招？',
    a: '六支球拍各帶一招必殺技：烈焰爆裂抽擊、天雷貫地、幻影分身、絕對零度、龍捲旋風、震地重砲，每招都有自己的破解法。每位選手另外有自己的絕招，例如秒蹲大力一揮、空中轉體扣殺的「幹他！」、噴水讓對手腳滑、雙拍曲球。接到對手的必殺球時打出「完美」就能破解。',
  },
  {
    q: '可以跟朋友一起玩嗎？',
    a: '目前是跟 AI 對戰（新手、球友、高手三種程度）。打完一場可以分享戰績圖，或傳挑戰連結給朋友，讓他用同樣的設定挑戰你的比分。',
  },
  {
    q: '適合新手用來學匹克球嗎？',
    a: '適合。地上的站位圈會提示該站哪裡，犯規時會說明規則，打過幾場就會知道雙彈跳、廚房和報分怎麼運作。想看完整規則可以到匹克球規則教學頁。',
  },
  {
    q: '「匹克硬邦幫」是誰做的？',
    a: '由「匹克大師台灣 Picklemaster Taiwan」製作，這個網站同時整理了全台匹克球場地圖、規則教學與裝備指南。',
  },
];

/** 遊戲特色（預渲染內文用；React 頁面有各自的視覺區塊呈現同樣內容） */
export const GAME_FEATURES = [
  { t: '免費、免下載、手機也能玩', d: '打開瀏覽器就能玩，手機觸控搖桿、電腦鍵盤都支援，開打後自動全螢幕。' },
  { t: '真實物理與比賽規則', d: '球的反彈、球拍反彈係數、空氣阻力與旋轉照真實數據；雙彈跳、廚房、換發球制照比賽判。' },
  { t: '六支球拍、六招必殺技', d: '烈焰爆裂抽擊、天雷貫地、幻影分身、絕對零度、龍捲旋風、震地重砲，每招都有自己的破解法。' },
  { t: '五位選手，各有絕招', d: '真人臉的 3D 選手，每人一招招牌打法。' },
  { t: '殺球、攔網、飛身撲接', d: '機會球跳起來殺、網前推擋截擊、跑不到的球撲過去救。' },
  { t: '無雙覺醒與電視輔助判決', d: '落後時發動覺醒逆轉；壓線球可以申請多角度重播加鷹眼判決。' },
  { t: '兩個場地、三種難度', d: '夜間體育館與新生公園羽毛球場；新手、球友、高手三種 AI，另有 Dink 挑戰模式。' },
];
